import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import RichTextEditor from '@/components/RichTextEditor';
import {
  Users, Loader2, RefreshCw, Plus, Trash2, Edit2, Send, Mail,
  UserPlus, Upload, Download, Sparkles, BarChart3
} from 'lucide-react';
import NewsletterDeliveryReportDialog from '@/components/NewsletterDeliveryReportDialog';

export const NewsletterSection = ({ api }) => {
  const [newsletters, setNewsletters] = useState([]);
  const [deliveryReport, setDeliveryReport] = useState({ open: false, newsletterId: null, subject: '' });
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [subscriberStats, setSubscriberStats] = useState(null);
  const [createNewsletterDialog, setCreateNewsletterDialog] = useState(false);
  const [editNewsletterDialog, setEditNewsletterDialog] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [sendingNewsletter, setSendingNewsletter] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [newNewsletter, setNewNewsletter] = useState({
    subject: '', content: '', preview_text: '', target_subscription: 'all', image_url: ''
  });
  const [aiNewsletterForm, setAiNewsletterForm] = useState({
    topic: '', tone: 'friendly', target_audience: 'parents', generate_image: true
  });
  const [nonCustomerSubscribers, setNonCustomerSubscribers] = useState([]);
  const [nonCustomerStats, setNonCustomerStats] = useState(null);
  const [nonCustomerLoading, setNonCustomerLoading] = useState(false);
  const [uploadEmailsDialog, setUploadEmailsDialog] = useState(false);
  const [emailUploadText, setEmailUploadText] = useState('');
  const [emailUploadSource, setEmailUploadSource] = useState('manual');
  const [emailUploadTags, setEmailUploadTags] = useState('');
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const [allSubscribersStats, setAllSubscribersStats] = useState(null);

  useEffect(() => {
    fetchNewsletters();
    fetchSubscriberStats();
    fetchNonCustomerSubscribers();
    fetchAllSubscribers();
    api.post('/admin/newsletter-subscribers/sync-customers?include_unverified=true').catch(() => {});
  }, []);

  const fetchNewsletters = async () => {
    setNewsletterLoading(true);
    try {
      const response = await api.get('/admin/newsletters');
      setNewsletters(response.data.newsletters || []);
    } catch (error) {
      console.error('Failed to fetch newsletters:', error);
    } finally {
      setNewsletterLoading(false);
    }
  };

  const fetchSubscriberStats = async () => {
    try {
      const response = await api.get('/admin/newsletters/subscriber-stats');
      setSubscriberStats(response.data);
    } catch (error) {
      console.error('Failed to fetch subscriber stats:', error);
    }
  };

  const fetchNonCustomerSubscribers = async () => {
    setNonCustomerLoading(true);
    try {
      const [subscribersRes, statsRes] = await Promise.all([
        api.get('/admin/newsletter-subscribers?limit=100'),
        api.get('/admin/newsletter-subscribers/stats')
      ]);
      setNonCustomerSubscribers(subscribersRes.data.subscribers || []);
      setNonCustomerStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch non-customer subscribers:', error);
    } finally {
      setNonCustomerLoading(false);
    }
  };

  const fetchAllSubscribers = async () => {
    try {
      const response = await api.get('/admin/newsletter-subscribers/all?limit=100');
      setAllSubscribersStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch all subscribers:', error);
    }
  };

  const handleSyncCustomers = async () => {
    setSyncingCustomers(true);
    try {
      const response = await api.post('/admin/newsletter-subscribers/sync-customers?include_unverified=true');
      toast.success(response.data.message);
      fetchSubscriberStats();
      fetchNonCustomerSubscribers();
      fetchAllSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync customers');
    } finally {
      setSyncingCustomers(false);
    }
  };

  const handleUploadEmails = async () => {
    const emails = emailUploadText.split(/[\n,;]+/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) { toast.error('Please enter at least one email'); return; }
    const tags = emailUploadTags.split(',').map(t => t.trim()).filter(t => t);
    try {
      const response = await api.post('/admin/newsletter-subscribers/upload', { emails, source: emailUploadSource, tags });
      toast.success(`Added ${response.data.added} subscribers! (${response.data.skipped} already existed, ${response.data.already_customers} are customers)`);
      setUploadEmailsDialog(false);
      setEmailUploadText('');
      setEmailUploadTags('');
      fetchNonCustomerSubscribers();
      fetchSubscriberStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload emails');
    }
  };

  const handleDeleteNonCustomer = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await api.delete(`/admin/newsletter-subscribers/${id}`);
      toast.success('Subscriber removed');
      fetchNonCustomerSubscribers();
      fetchSubscriberStats();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleToggleSubscription = async (id, currentStatus) => {
    try {
      await api.put(`/admin/newsletter-subscribers/${id}?subscribed=${!currentStatus}`);
      toast.success(currentStatus ? 'Unsubscribed' : 'Resubscribed');
      fetchNonCustomerSubscribers();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleExportNonCustomers = async () => {
    try {
      const response = await api.post('/admin/newsletter-subscribers/export');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'non_customer_subscribers.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${response.data.count} subscribers`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleCreateNewsletter = async () => {
    if (!newNewsletter.subject || !newNewsletter.content) { toast.error('Please fill in subject and content'); return; }
    try {
      await api.post('/admin/newsletters', newNewsletter);
      toast.success('Newsletter draft created!');
      setCreateNewsletterDialog(false);
      setNewNewsletter({ subject: '', content: '', preview_text: '', target_subscription: 'all', image_url: '' });
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create newsletter');
    }
  };

  const handleUpdateNewsletter = async () => {
    if (!editingNewsletter) return;
    try {
      await api.put(`/admin/newsletters/${editingNewsletter.id}`, {
        subject: editingNewsletter.subject, content: editingNewsletter.content,
        preview_text: editingNewsletter.preview_text, target_subscription: editingNewsletter.target_subscription,
        image_url: editingNewsletter.image_url
      });
      toast.success('Newsletter updated!');
      setEditNewsletterDialog(false);
      setEditingNewsletter(null);
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update newsletter');
    }
  };

  const handleDeleteNewsletter = async (id) => {
    if (!window.confirm('Delete this newsletter draft?')) return;
    try {
      await api.delete(`/admin/newsletters/${id}`);
      toast.success('Newsletter deleted');
      fetchNewsletters();
    } catch (error) {
      toast.error('Failed to delete newsletter');
    }
  };

  const handleSendNewsletter = async (id) => {
    const newsletter = newsletters.find(n => n.id === id);
    const targetText = newsletter?.target_subscription === 'all' ? 'all subscribers' : `${newsletter?.target_subscription} subscribers`;
    if (!window.confirm(`Send this newsletter to ${targetText}? This cannot be undone.`)) return;
    setSendingNewsletter(id);
    try {
      const response = await api.post(`/admin/newsletters/${id}/send`);
      toast.success(response.data.message || 'Newsletter is being sent!');
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSendingNewsletter(null);
    }
  };

  const handleGenerateAINewsletter = async () => {
    if (!aiNewsletterForm.topic) { toast.error('Please enter a topic'); return; }
    setGeneratingAI(true);
    try {
      const response = await api.post('/admin/newsletters/generate-ai', {
        topic: aiNewsletterForm.topic, tone: aiNewsletterForm.tone,
        target_audience: aiNewsletterForm.target_audience, generate_image: aiNewsletterForm.generate_image,
        include_sections: ['intro', 'main_content', 'call_to_action']
      });
      setNewNewsletter({
        subject: response.data.subject || '', content: response.data.content || '',
        preview_text: response.data.preview_text || '', target_subscription: 'all',
        image_url: response.data.image_url || ''
      });
      toast.success('AI newsletter generated! Review and edit before sending.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate newsletter');
    } finally {
      setGeneratingAI(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Newsletters & Bulk Email</h1>
          <p className="text-slate-600">Send newsletters to subscribers by subscription tier</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncCustomers} disabled={syncingCustomers}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="sync-customers-btn">
            {syncingCustomers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {syncingCustomers ? 'Syncing...' : 'Sync All Customers'}
          </Button>
          <Button onClick={() => setCreateNewsletterDialog(true)} data-testid="create-newsletter-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Newsletter
          </Button>
        </div>
      </div>

      {allSubscribersStats && (
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-800">Master Subscriber List</h3>
                  <p className="text-sm text-emerald-600">All customers and non-customers synced to newsletter</p>
                </div>
              </div>
              <div className="flex gap-6 text-center">
                <div><p className="text-3xl font-bold text-emerald-700">{allSubscribersStats.total_all}</p><p className="text-xs text-emerald-600">Total Subscribers</p></div>
                <div><p className="text-2xl font-bold text-blue-600">{allSubscribersStats.total_customers}</p><p className="text-xs text-blue-500">Customers</p></div>
                <div><p className="text-2xl font-bold text-pink-600">{allSubscribersStats.total_non_customers}</p><p className="text-xs text-pink-500">Non-Customers</p></div>
                <div><p className="text-2xl font-bold text-green-600">{allSubscribersStats.total_subscribed}</p><p className="text-xs text-green-500">Subscribed</p></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {subscriberStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {[
            { label: 'Customers', value: subscriberStats.subscribers?.all, color: 'from-slate-50 to-slate-100', text: 'text-slate-800' },
            { label: 'Non-Customers', value: subscriberStats.non_customers?.subscribed, color: 'from-pink-50 to-pink-100 border-2 border-pink-200', text: 'text-pink-600' },
            { label: 'Free', value: subscriberStats.subscribers?.free, color: 'from-gray-50 to-gray-100', text: 'text-gray-600' },
            { label: 'Basic', value: subscriberStats.subscribers?.basic, color: 'from-blue-50 to-blue-100', text: 'text-blue-600' },
            { label: 'Family', value: subscriberStats.subscribers?.family, color: 'from-emerald-50 to-emerald-100', text: 'text-emerald-600' },
            { label: 'Premium', value: subscriberStats.subscribers?.premium, color: 'from-amber-50 to-amber-100', text: 'text-amber-600' },
            { label: 'Enterprise', value: subscriberStats.subscribers?.enterprise, color: 'from-purple-50 to-purple-100', text: 'text-purple-600' },
            { label: 'Grand Total', value: subscriberStats.grand_total, color: 'from-indigo-50 to-indigo-100', text: 'text-indigo-600' },
          ].map((s) => (
            <Card key={s.label} className={cn("bg-gradient-to-br", s.color)}>
              <CardContent className="p-4 text-center">
                <p className={cn("text-2xl font-bold", s.text)}>{s.value || 0}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>All Newsletters</CardTitle></CardHeader>
        <CardContent>
          {newsletterLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : newsletters.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-2">No newsletters yet</p>
              <Button onClick={() => setCreateNewsletterDialog(true)}><Plus className="w-4 h-4 mr-2" /> Create Newsletter</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {newsletters.map((newsletter) => (
                <Card key={newsletter.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{newsletter.subject}</h3>
                          <span className={cn("px-2 py-0.5 rounded text-xs font-medium",
                            newsletter.status === 'sent' ? "bg-green-100 text-green-700" :
                            newsletter.status === 'sending' ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                          )}>{newsletter.status === 'sent' ? 'Sent' : newsletter.status === 'sending' ? 'Sending...' : 'Draft'}</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                            Target: {newsletter.target_subscription === 'all' ? 'All Subscribers' : newsletter.target_subscription}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">{newsletter.preview_text}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>Created: {new Date(newsletter.created_at).toLocaleString()}</span>
                          {newsletter.sent_at && <span>Sent: {new Date(newsletter.sent_at).toLocaleString()}</span>}
                          {newsletter.recipients_count > 0 && (
                            <span className="text-emerald-600">Recipients: {newsletter.recipients_count}{newsletter.success_count !== undefined && ` (${newsletter.success_count} delivered)`}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {newsletter.status === 'sent' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeliveryReport({ open: true, newsletterId: newsletter.id, subject: newsletter.subject })}
                            data-testid={`view-delivery-report-${newsletter.id}`}
                          >
                            <BarChart3 className="w-4 h-4 mr-1" /> Delivery report
                          </Button>
                        )}
                        {newsletter.status === 'draft' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => { setEditingNewsletter({...newsletter}); setEditNewsletterDialog(true); }}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => handleSendNewsletter(newsletter.id)} disabled={sendingNewsletter === newsletter.id} className="bg-emerald-600 hover:bg-emerald-700">
                              {sendingNewsletter === newsletter.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteNewsletter(newsletter.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Non-Customer Subscribers */}
      <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50/50 to-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-pink-700 flex items-center gap-2"><UserPlus className="w-5 h-5" /> Non-Customer Subscribers</CardTitle>
            <p className="text-sm text-pink-500 mt-1">Upload emails of potential customers for newsletter campaigns</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportNonCustomers} className="border-pink-300 text-pink-600 hover:bg-pink-50">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button onClick={() => setUploadEmailsDialog(true)} className="bg-pink-600 hover:bg-pink-700">
              <Upload className="w-4 h-4 mr-2" /> Upload Emails
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {nonCustomerLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-pink-500" /></div>
          ) : nonCustomerSubscribers.length === 0 ? (
            <div className="text-center py-12 text-pink-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No non-customer subscribers yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {nonCustomerSubscribers.map((sub) => (
                <div key={sub.id} className={`flex items-center justify-between p-3 rounded-lg border ${sub.subscribed ? 'bg-white border-pink-100' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${sub.subscribed ? 'bg-pink-100 text-pink-600' : 'bg-gray-200 text-gray-500'}`}>
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`font-medium ${sub.subscribed ? 'text-slate-800' : 'text-gray-500'}`}>{sub.email}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {sub.name && <span>{sub.name}</span>}
                        <span className="text-pink-400">&#8226;</span>
                        <span className="capitalize">{sub.source || 'manual'}</span>
                        {sub.tags?.length > 0 && (
                          <>{sub.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs py-0 border-pink-200 text-pink-600">{tag}</Badge>)}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleToggleSubscription(sub.id, sub.subscribed)}
                      className={sub.subscribed ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}>
                      {sub.subscribed ? 'Unsubscribe' : 'Resubscribe'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteNonCustomer(sub.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {nonCustomerStats && (
            <div className="mt-4 pt-4 border-t border-pink-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div><p className="text-2xl font-bold text-pink-600">{nonCustomerStats.total}</p><p className="text-xs text-pink-400">Total</p></div>
              <div><p className="text-2xl font-bold text-emerald-600">{nonCustomerStats.subscribed}</p><p className="text-xs text-emerald-500">Subscribed</p></div>
              <div><p className="text-2xl font-bold text-amber-600">{nonCustomerStats.unsubscribed}</p><p className="text-xs text-amber-500">Unsubscribed</p></div>
              <div>
                <p className="text-sm font-medium text-slate-600">Top Sources:</p>
                <div className="flex flex-wrap gap-1 justify-center mt-1">
                  {Object.entries(nonCustomerStats.by_source || {}).slice(0, 3).map(([source, count]) => (
                    <Badge key={source} variant="outline" className="text-xs">{source}: {count}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Emails Dialog */}
      <Dialog open={uploadEmailsDialog} onOpenChange={setUploadEmailsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-pink-600" /> Upload Non-Customer Emails</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Addresses</Label>
              <Textarea placeholder={"Enter emails (one per line, or comma/semicolon separated)\n\nexample1@email.com\nexample2@email.com"} value={emailUploadText} onChange={(e) => setEmailUploadText(e.target.value)} className="min-h-[150px] font-mono text-sm" />
              <p className="text-xs text-slate-500 mt-1">{emailUploadText.split(/[\n,;]+/).filter(e => e.trim()).length} emails detected</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source</Label>
                <Select value={emailUploadSource} onValueChange={setEmailUploadSource}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Entry</SelectItem>
                    <SelectItem value="import">CSV Import</SelectItem>
                    <SelectItem value="website">Website Form</SelectItem>
                    <SelectItem value="event">Event/Webinar</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags (optional)</Label>
                <Input placeholder="e.g., lead, interested" value={emailUploadTags} onChange={(e) => setEmailUploadTags(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadEmailsDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadEmails} className="bg-pink-600 hover:bg-pink-700"><Upload className="w-4 h-4 mr-2" /> Upload Emails</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Newsletter Dialog */}
      <Dialog open={createNewsletterDialog} onOpenChange={setCreateNewsletterDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Newsletter</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-4"><Sparkles className="w-5 h-5" /> AI Newsletter Generator</h3>
                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-purple-700">Topic</Label>
                    <Input placeholder="e.g., Back to school tips" value={aiNewsletterForm.topic} onChange={(e) => setAiNewsletterForm({...aiNewsletterForm, topic: e.target.value})} className="bg-white" />
                  </div>
                  <div>
                    <Label className="text-purple-700">Tone</Label>
                    <Select value={aiNewsletterForm.tone} onValueChange={(v) => setAiNewsletterForm({...aiNewsletterForm, tone: v})}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="exciting">Exciting</SelectItem>
                        <SelectItem value="educational">Educational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-700">Audience</Label>
                    <Select value={aiNewsletterForm.target_audience} onValueChange={(v) => setAiNewsletterForm({...aiNewsletterForm, target_audience: v})}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parents">Parents</SelectItem>
                        <SelectItem value="kids">Kids</SelectItem>
                        <SelectItem value="teachers">Teachers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleGenerateAINewsletter} disabled={generatingAI || !aiNewsletterForm.topic} className="w-full bg-purple-600 hover:bg-purple-700">
                      {generatingAI ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input type="checkbox" id="generate-image" checked={aiNewsletterForm.generate_image} onChange={(e) => setAiNewsletterForm({...aiNewsletterForm, generate_image: e.target.checked})} className="accent-purple-600" />
                  <label htmlFor="generate-image" className="text-sm text-purple-700">Also generate a header image</label>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Subject Line *</Label><Input placeholder="Your newsletter subject" value={newNewsletter.subject} onChange={(e) => setNewNewsletter({...newNewsletter, subject: e.target.value})} /></div>
                <div>
                  <Label>Target Audience</Label>
                  <Select value={newNewsletter.target_subscription} onValueChange={(v) => setNewNewsletter({...newNewsletter, target_subscription: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      <SelectItem value="non_customers">Non-Customers Only</SelectItem>
                      <SelectItem value="all_including_non_customers">Everyone</SelectItem>
                      <SelectItem value="free">Free Users Only</SelectItem>
                      <SelectItem value="basic">Basic Subscribers</SelectItem>
                      <SelectItem value="family">Family Subscribers</SelectItem>
                      <SelectItem value="premium">Premium Subscribers</SelectItem>
                      <SelectItem value="enterprise">Enterprise Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Preview Text</Label><Input placeholder="Short preview shown in email clients" value={newNewsletter.preview_text} onChange={(e) => setNewNewsletter({...newNewsletter, preview_text: e.target.value})} /></div>
              {newNewsletter.image_url && (
                <div><Label>Generated Image</Label><div className="mt-2 rounded-lg overflow-hidden border"><img src={newNewsletter.image_url} alt="Newsletter" className="w-full max-h-48 object-cover" /></div></div>
              )}
              <div>
                <Label>Content *</Label>
                <p className="text-xs text-slate-500 mb-2">Use {'{{name}}'} to personalize with recipient's name.</p>
                <RichTextEditor value={newNewsletter.content} onChange={(content) => setNewNewsletter({...newNewsletter, content})} placeholder="Write your newsletter content here..." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateNewsletterDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateNewsletter} disabled={!newNewsletter.subject || !newNewsletter.content}>Save as Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Newsletter Dialog */}
      <Dialog open={editNewsletterDialog} onOpenChange={setEditNewsletterDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Newsletter</DialogTitle></DialogHeader>
          {editingNewsletter && (
            <div className="space-y-4 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Subject Line</Label><Input value={editingNewsletter.subject} onChange={(e) => setEditingNewsletter({...editingNewsletter, subject: e.target.value})} /></div>
                <div>
                  <Label>Target Audience</Label>
                  <Select value={editingNewsletter.target_subscription} onValueChange={(v) => setEditingNewsletter({...editingNewsletter, target_subscription: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      <SelectItem value="non_customers">Non-Customers Only</SelectItem>
                      <SelectItem value="all_including_non_customers">Everyone</SelectItem>
                      <SelectItem value="free">Free Users Only</SelectItem>
                      <SelectItem value="basic">Basic Subscribers</SelectItem>
                      <SelectItem value="family">Family Subscribers</SelectItem>
                      <SelectItem value="premium">Premium Subscribers</SelectItem>
                      <SelectItem value="enterprise">Enterprise Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Preview Text</Label><Input value={editingNewsletter.preview_text} onChange={(e) => setEditingNewsletter({...editingNewsletter, preview_text: e.target.value})} /></div>
              <div><Label>Content</Label><RichTextEditor value={editingNewsletter.content} onChange={(content) => setEditingNewsletter({...editingNewsletter, content})} placeholder="Edit your newsletter content..." /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNewsletterDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateNewsletter}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery report modal */}
      <NewsletterDeliveryReportDialog
        open={deliveryReport.open}
        onOpenChange={(o) => setDeliveryReport((d) => ({ ...d, open: o }))}
        newsletterId={deliveryReport.newsletterId}
        newsletterSubject={deliveryReport.subject}
      />
    </div>
  );
};
